import './confirmation-page.css';
import { Alert, Button } from 'antd';
import { CaseInfo, Slot } from '../../../dto/model';
import { strings } from '../../../lang';
import CustomerInfoSection from './online-booking-page-cust-info';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import CaseCommentSection from './online-booking-page-case-comments';

export interface ConfirmationPageProps {
  caseInfo: CaseInfo;
  bookedSlot: Slot;
  cancelledSlot: Slot | null;
  inputCaseComments: string;
  setInputCaseComments: (inputComments: string) => void;
  onOpenNextCase: (caseComment: string) => void;
  isOpeningNextCase?: boolean;
  inboundCenter?: boolean | undefined;
  isAutodialerFlow?: boolean;
  onBackToInbound?: (isSuccess: boolean) => void;
  handleCallButtonClick: (phoneNumber: string) => void;
}

export const ConfirmationPage = ({
  caseInfo,
  bookedSlot,
  cancelledSlot,
  inputCaseComments,
  setInputCaseComments,
  onOpenNextCase,
  isOpeningNextCase,
  inboundCenter,
  isAutodialerFlow,
  onBackToInbound,
  handleCallButtonClick,
}: ConfirmationPageProps) => {
  // Auto-advance to the next case is disabled for flows where the agent has to
  // explicitly release the case (inbound center, autodialer). For those flows we
  // start at -1 so the countdown effect never schedules a timer, eliminating the
  // race between the "reset to -1" effect and the "tick" effect on mount.
  const isCountdownDisabled = !!(inboundCenter || isAutodialerFlow);
  const [counter, setCounter] = useState<number>(isCountdownDisabled ? -1 : 180);

  useEffect(() => {
    setCounter(isCountdownDisabled ? -1 : 180);
  }, [caseInfo, isCountdownDisabled]);

  useEffect(() => {
    if (isCountdownDisabled) return;
    if (counter < 0) return;
    if (counter === 0) {
      if (isOpeningNextCase) return;
      onOpenNextCase(inputCaseComments);
      return;
    }
    const timer = setTimeout(() => setCounter(counter - 1), 1000);
    return () => clearTimeout(timer);
  }, [counter, isCountdownDisabled]);

  const onOpenNextCaseClicked = () => {
    if (isOpeningNextCase) return;
    setCounter(-1);
    onOpenNextCase(inputCaseComments);
  };

  const cancelCountdown = () => {
    setCounter(-1);
  };

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div className="cp-booking-info">
      <section className="cp-section">
        <Alert type="success" showIcon={true} message={strings.bookingSuccessful} />
      </section>

      <section className="cp-section">
        <CustomerInfoSection
          inboundCenter={inboundCenter}
          isAutodialerFlow={isAutodialerFlow}
          caseInfo={caseInfo}
          cancelledSlot={cancelledSlot}
          showWrongPhoneNumberModal={(val: boolean) => {}}
          handleCallButtonClick={handleCallButtonClick}
        />
      </section>

      <section className="cp-section">
        <CaseCommentSection caseComments={caseInfo.caseComments} inputCaseComment={inputCaseComments} onInputCaseCommentChange={setInputCaseComments} shouldShowPreviousComments={true} />
      </section>

      <section className="cp-section">
        <h4 className="confirmation-page__title">{strings.appointmentDetails}</h4>
        <ul>
          <li>
            {strings.salesManager}: <b>{bookedSlot?.salesManagerName}</b>
          </li>
          <li>
            {strings.date}: <b>{dayjs(bookedSlot?.startDate).utc().tz('Europe/Berlin').format('DD.MM.YYYY')}</b>
          </li>
          <li>
            {strings.time}: <b>{dayjs(bookedSlot?.startDate).utc().tz('Europe/Berlin').format('HH:mm')}</b>
          </li>
        </ul>
        <div className="confirmation-page__buttons">
          {counter >= 0 && <span> {strings.formatString(strings.openingNextCaseIn, formatTime(counter))}</span>}
          {!inboundCenter && !isAutodialerFlow && <Button onClick={cancelCountdown}>{strings.cancelCountdown}</Button>}
          {!inboundCenter && !isAutodialerFlow && (
            <Button type="primary" onClick={onOpenNextCaseClicked} loading={isOpeningNextCase} disabled={isOpeningNextCase}>
              {strings.openNextCase}
            </Button>
          )}
          {inboundCenter && !isAutodialerFlow && (
            <Button
              type="primary"
              onClick={() => {
                onBackToInbound!(true);
              }}
            >
              {strings.backToInboundCenter}
            </Button>
          )}
          {isAutodialerFlow && (
            <Button type="primary" onClick={onOpenNextCaseClicked} loading={isOpeningNextCase} disabled={isOpeningNextCase}>
              {strings.autodialerCaseComplete}
            </Button>
          )}
        </div>
      </section>
    </div>
  );
};
